import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export default function RegisterPage() {
  const [form, setForm] = useState({
    name: '', mobile: '', email: '',
    agency_name: '', oil_company: '', sales_person_name: '',
    company_name: '',
    address: '', distributor_code: '', password: '', role: 'distributor',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Owner Name is required'); return; }
    if (!form.mobile.trim()) { setError('Mobile Number is required'); return; }
    if (!form.email.trim()) { setError('Email Address is required'); return; }
    if (!form.agency_name.trim()) { setError('Agency Name is required'); return; }
    if (!form.oil_company.trim()) { setError('Oil Company select karein'); return; }
    if (!form.sales_person_name.trim()) { setError('Sales Person Name is required'); return; }
    if (!form.address.trim()) { setError('Address is required'); return; }
    if (!form.password || form.password.length < 6) { setError('Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      // Send company_name=agency_name for backward compat; plus new distributor fields
      const payload = { ...form, company_name: form.company_name || form.agency_name };
      const res = await register(payload);
      setSuccess(true);
      if (res?.resolved_sales_person) {
        setSuccessMsg(`Sales Person auto-assigned: ${res.resolved_sales_person}. Redirecting...`);
      } else if (form.sales_person_name) {
        setSuccessMsg(`Sales Person "${form.sales_person_name}" admin review ke liye save ho gaya. Redirecting...`);
      }
      setTimeout(() => navigate('/login?registered=true'), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-gray-200 shadow-sm overflow-hidden rounded" data-testid="register-page">
        {/* Header Banner — matches Login page */}
        <div className="bg-[#0C1D3F] px-6 py-5 text-center">
          <h1 className="text-lg sm:text-xl font-bold text-white tracking-wide leading-tight">AXIALUP DIGITAL SERVICES</h1>
          <p className="text-[10px] sm:text-[11px] text-blue-300 mt-1.5 tracking-widest uppercase">Secure Digital Payment & Booking Platform</p>
        </div>

        <div className="p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Distributor Registration</h2>
          <p className="text-xs text-gray-500 mb-5"><span className="text-red-500">*</span> indicates required fields</p>

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm" data-testid="register-success">
            Registration successful! {successMsg || 'Account pending admin approval. Redirecting...'}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
            {error}
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <Label htmlFor="name" className="text-sm font-medium">Owner Name <span className="text-red-500">*</span></Label>
              <Input id="name" type="text" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Enter owner name" required data-testid="register-name-input" />
            </div>

            <div>
              <Label htmlFor="mobile" className="text-sm font-medium">Mobile Number <span className="text-red-500">*</span></Label>
              <Input id="mobile" type="tel" value={form.mobile} onChange={(e) => update('mobile', e.target.value)} placeholder="Enter mobile number" required data-testid="register-mobile-input" />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email Address <span className="text-red-500">*</span></Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="Enter email address" required data-testid="register-email-input" />
            </div>

            <div>
              <Label htmlFor="agency_name" className="text-sm font-medium">Agency Name <span className="text-red-500">*</span></Label>
              <Input id="agency_name" type="text" value={form.agency_name} onChange={(e) => update('agency_name', e.target.value)} required data-testid="register-agency-input" />
            </div>

            <div>
              <Label htmlFor="oil_company" className="text-sm font-medium">Oil Company <span className="text-red-500">*</span></Label>
              <select
                id="oil_company"
                value={form.oil_company}
                onChange={(e) => update('oil_company', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                required
                data-testid="register-oil-company-select"
              >
                <option value="">-- Select --</option>
                <option value="Indane">Indane</option>
                <option value="Bharat Gas">Bharat Gas</option>
                <option value="HP Gas">HP Gas</option>
              </select>
            </div>

            <div>
              <Label htmlFor="sales_person_name" className="text-sm font-medium">Sales Person Name <span className="text-red-500">*</span></Label>
              <Input id="sales_person_name" type="text" value={form.sales_person_name} onChange={(e) => update('sales_person_name', e.target.value)} required data-testid="register-sp-input" />
            </div>

            <div>
              <Label htmlFor="address" className="text-sm font-medium">Address <span className="text-red-500">*</span></Label>
              <Textarea id="address" value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="Enter full address" rows={2} required data-testid="register-address-input" />
            </div>

            <div>
              <Label htmlFor="distributor_code" className="text-sm font-medium">Distributor Code <span className="text-gray-400 text-xs font-normal">(Optional)</span></Label>
              <Input id="distributor_code" type="text" value={form.distributor_code} onChange={(e) => update('distributor_code', e.target.value)} placeholder="Enter distributor code (if any)" data-testid="register-distcode-input" />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password <span className="text-red-500">*</span></Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => update('password', e.target.value)} placeholder="Min 6 characters" required minLength={6} data-testid="register-password-input" />
            </div>

            <Button type="submit" className="w-full bg-[#002FA7] hover:bg-[#001F6F] h-11 mt-1" disabled={loading} data-testid="register-submit-btn">
              {loading ? 'Creating account...' : 'Register'}
            </Button>
          </form>
        )}

        <p className="mt-5 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-[#002FA7] font-medium hover:underline" data-testid="login-link">Login</Link>
        </p>

        <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
          <Link to="/terms" className="hover:text-[#002FA7] hover:underline">Terms</Link>
          <span>|</span>
          <Link to="/privacy" className="hover:text-[#002FA7] hover:underline">Privacy</Link>
          <span>|</span>
          <Link to="/refund-policy" className="hover:text-[#002FA7] hover:underline">Refund Policy</Link>
          <span>|</span>
          <Link to="/support-help" className="hover:text-[#002FA7] hover:underline">Support</Link>
        </div>
        </div>
      </div>
    </div>
  );
}
